import React, { createContext, useContext, useEffect, useState, Dispatch, SetStateAction } from 'react';


export interface ContactContextType {
  getAuthNextPage: () => string | null;
  setAuthNextPage:  (page:string) => void;
  contact: Contact | null;
  family: FamilyMember[] | null;
  setContact: Dispatch<SetStateAction<Contact | null>>;
  setFamily: Dispatch<SetStateAction<FamilyMember[] | null>>;
  cart: CartItem[];
  setCart: Dispatch<SetStateAction<CartItem[]>>;
}

const ContactContext = createContext<ContactContextType | undefined>(undefined);

export const useContactType = (): ContactContextType => {
  const context = useContext(ContactContext);
  if (!context) {
    throw new Error('useContactType must be used within an ContactProvider');
  }
  return context;
};

interface ContactProviderProps {
  children: React.ReactNode;
}

export const ContactProvider: React.FC<ContactProviderProps> = ({ children }) => {
  const [contact, setContact] = useState<Contact | null>(() => {
    // Retrieve the contact from localStorage when the component mounts
    const storedContact = localStorage.getItem('contact');
    return storedContact ? JSON.parse(storedContact) : null;
  });

  const [nextPage, setNextPage] = useState<string | null>(null);

  const [family, setFamily] = useState<FamilyMember[] | null>(() => {
    // Retrieve the contact from localStorage when the component mounts
    const storedFamily = localStorage.getItem('family');
    return storedFamily ? JSON.parse(storedFamily) : null;
  });
  const [cart, setCart] = useState<CartItem[]>(() => {
    // Retrieve the cart from localStorage when the component mounts
    const storedCart = localStorage.getItem('cart');
    return storedCart ? JSON.parse(storedCart) : [];
  });

  useEffect(() => {
    if (contact) {
      // Save the contact data in localStorage whenever it changes
      localStorage.setItem('contact', JSON.stringify(contact));
    } else {
      // Remove from localStorage if contact is null (e.g., logout)
      localStorage.removeItem('contact');
    }
  }, [contact]);

  useEffect(() => {
    if (family) {
      // Save the contact data in localStorage whenever it changes
      localStorage.setItem('family', JSON.stringify(family));
    } else {
      // Remove from localStorage if contact is null (e.g., logout)
      localStorage.removeItem('family');
    }
  }, [contact]);

  const setAuthNextPage = (page: string) => {
    setNextPage(page);
  }
  const getAuthNextPage = () => {
    const page = nextPage;
    setNextPage(null);
    return page;
  }

  const contextValue: ContactContextType = {
    cart,
    setCart,
    getAuthNextPage,
    setAuthNextPage,
    contact,
    setContact,
    family,
    setFamily

  };

  return (
    <ContactContext.Provider value={contextValue}>
      {children}
    </ContactContext.Provider>
  );
};
